import clsx from "clsx";
import { format, formatISO } from "date-fns";
import type { FC, Ref } from "react";

import { ChatBubble } from "./Bubble";
import type { MentionTarget } from "./mentions";

import { ChatMessageType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuItem } from "mds/components/Menu";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { DotMenuHorizontalIcon, StarFilledIcon, TrashDeleteIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentUserId, userSelectors } from "store/selectors";
import { getUserFullName } from "utils/user-utils";
import { getUserColor } from "worksheets/helpers/colors";

export type UserChatBubbleProps = {
  className?: string;
  ref?: Ref<HTMLDivElement>;
  mentionTargets?: MentionTarget[];
  message: ChatMessageType;
  parseMarkdown?: boolean;
  showStar?: boolean;
};

export const UserChatBubble: FC<UserChatBubbleProps> = ({
  ref,
  className,
  mentionTargets,
  message,
  parseMarkdown = false,
  showStar = false,
}) => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const { canDeleteChatMessage } = useCapabilities();
  const authorUser = useAppSelector((s) => userSelectors.selectById(s, message.author_id));

  // Note that `userColor` is *ignored* if `isSelf` is true.
  // As a special case, we always want the current user to be displayed in blue.
  const userColor = authorUser ? getUserColor(authorUser.id) : undefined;
  const isSelf = authorUser?.id === currentUserId;

  const header = (
    <div className="flex items-center gap-2">
      <div
        className={clsx("text-sm font-semibold", isSelf && "text-blue")}
        style={!isSelf && userColor ? { color: userColor } : undefined}
      >
        {getUserFullName(authorUser)}
      </div>
      {showStar && <StarFilledIcon className="size-3 text-black-tint-70" />}
      <time
        className="text-xs font-normal text-black-tint-40"
        dateTime={formatISO(message.created_at)}
      >
        {format(message.created_at, "hh:mm a")}
      </time>
      {canDeleteChatMessage && (
        <>
          <div className="grow" /> {/* spacer */}
          <Menu
            menuButton={
              <SquareIconButton aria-label={t("common.toggle_menu")} kind="ghost" size="s">
                <DotMenuHorizontalIcon />
              </SquareIconButton>
            }
          >
            <MenuItem
              eventName="Class Chat - Delete message"
              eventProps={{ authorName: getUserFullName(authorUser), message: message.text }}
              kind="destructive"
              onClick={() => storeApi.chat_messages.destroy(message.id)}
            >
              <IconText
                className="text-red"
                iconStart={<TrashDeleteIcon />}
                text={t("common.delete")}
              />
            </MenuItem>
          </Menu>
        </>
      )}
    </div>
  );

  return (
    <ChatBubble
      className={className}
      header={header}
      isSelf={isSelf}
      mentionTargets={mentionTargets}
      messageId={message.id}
      parseMarkdown={parseMarkdown}
      ref={ref}
    >
      {message.text}
    </ChatBubble>
  );
};
